import { inject } from '@angular/core';
import { PriceService } from '../core/services/price.service';
import { PriceState, UserFactory } from './price.model';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { filter, pipe, switchMap, tap } from 'rxjs';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { TableParams } from '../shared/components/table/models/page.model';
import { INITIAL_PAGINATED_RESPONSE } from '../core/constants/table-columns.const';
import { PriceComponentResult, SetPricePayload } from '../core/models/price.model';
import { ToastService } from '../core/services/toast.service';
import { SharedDataService } from '../core/services/shared-data.service';
import { AssociationTypesEnum } from '../core/enums/association-types.enum';
import { Role } from './shared.model';
import { PricingRoleTypesEnum } from '../core/enums/pricing-role-types.enum';
import saveAs from 'file-saver';
import { UtilityService } from '../core/services/utility.service';

const initialState: PriceState = {
  roles: [],
  timezone: '',
  userFactories: [],
  associationValue: '',
  priceComponents: [],
  paginatedPriceList: INITIAL_PAGINATED_RESPONSE(),
  selectedPrices: [],
  isPriceSetInProgress: false,
  isPriceSetDone: false,
  refreshPriceTable: false,
  isPriceListInitialized: false,
  isPriceTableLoading: false,
  isRolesInitialized: false,
  isPriceComponentsInitialized: false,
  isDataAdmin: false,
  isPriceDeleting: false,
  isPriceDeleteDone: false,
  isPriceReportDownloading: false,
  priceActiveFilters: {}
};

let tableParams: TableParams = { page: 1, pageSize: 10 };

export const PriceStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods(
    (
      store,
      priceService = inject(PriceService),
      sharedDataService = inject(SharedDataService),
      toastService = inject(ToastService),
      utilityService = inject(UtilityService)
    ) => ({
      getPreRequisiteForPriceList: rxMethod<void>(
        pipe(
          switchMap(() =>
            sharedDataService.getRoles(AssociationTypesEnum.FACTORY_CODE).pipe(
              tap((roles: Role[]) => {
                patchState(store, {
                  isRolesInitialized: true,
                  roles: roles,
                  associationValue: roles[0]?.associationValue,
                  isDataAdmin: roles.some((role) => role.name.toUpperCase() === PricingRoleTypesEnum.FACTORY_ADMIN)
                });
              }),
              filter((roles) => roles.length > 0)
            )
          ),
          switchMap(() => {
            const association = store.associationValue();
            return sharedDataService.getUserFactories(association).pipe(
              tap((userFactories: UserFactory[]) => {
                patchState(store, {
                  userFactories,
                  timezone: userFactories[0]?.timezone || 'Asia/Singapore'
                });
              }),
              switchMap(() => {
                return priceService.getPriceComponents(association);
              })
            );
          }),
          tapResponse({
            next: (priceComponents) => {
              patchState(store, {
                priceComponents: priceComponents,
                isPriceComponentsInitialized: true
              });
            },
            error: () => null
          })
        )
      ),
      getPaginatedPriceList: rxMethod<{ params: TableParams }>(
        pipe(
          tap(({ params }) => {
            tableParams = params;
          }),
          switchMap(() => {
            const priceComponentId = store.priceComponents().length ? store.priceComponents()[0].priceComponentId : '';
            return priceService.getPaginatedPriceList({
              page: tableParams.page,
              pageSize: tableParams.pageSize!,
              filters: tableParams?.filters,
              sort: tableParams?.sort,
              priceComponentId
            });
          }),
          tapResponse({
            next: (res) => {
              patchState(store, {
                paginatedPriceList: res,
                priceActiveFilters: { sort: tableParams?.sort, filters: tableParams?.filters },
                isPriceListInitialized: true,
                isPriceTableLoading: false
              });
            },
            error: () => {}
          })
        )
      ),
      setPrice: rxMethod<{ payload: SetPricePayload }>(
        pipe(
          tap(() => {
            patchState(store, {
              isPriceSetInProgress: true,
              isPriceSetDone: false
            });
          }),
          switchMap(({ payload }) => priceService.setPrice(payload)),
          tapResponse({
            next: () => {
              toastService.emitToast({
                isSuccess: true,
                message: 'Daily pricing have been added successfully'
              });

              patchState(store, {
                refreshPriceTable: true,
                isPriceSetInProgress: false,
                isPriceSetDone: true,
                isPriceTableLoading: true
              });

              setTimeout(() => {
                patchState(store, {
                  refreshPriceTable: false,
                  isPriceSetDone: false
                });
              }, 500);
            },
            error: () => {}
          })
        )
      ),
      deletePrice: rxMethod<{ priceId: number }>(
        pipe(
          tap(() => {
            patchState(store, { isPriceDeleting: true });
          }),
          switchMap(({ priceId }) =>
            priceService.deletePrice({
              priceComponentId: store.priceComponents()[0].priceComponentId || '',
              priceId: priceId
            })
          ),
          tapResponse({
            next: (res) => {
              toastService.emitToast({ isSuccess: true, message: res.message });
              patchState(store, {
                isPriceDeleting: false,
                refreshPriceTable: true,
                isPriceTableLoading: true,
                isPriceDeleteDone: true,
                selectedPrices: []
              });

              setTimeout(() => {
                patchState(store, { isPriceDeleteDone: false, refreshPriceTable: false });
              }, 500);
            },
            error: () => {}
          })
        )
      ),
      bulkDeeletePrice: rxMethod<{ priceIds: number[] }>(
        pipe(
          tap(() => {
            patchState(store, { isPriceDeleting: true });
          }),
          switchMap(({ priceIds }) =>
            priceService.bulkDeletePrice({
              priceIds: priceIds,
              priceComponentId: store.priceComponents()[0].priceComponentId || ''
            })
          ),
          tapResponse({
            next: (res) => {
              // TODO: handle array of emit toasts. need to config in ARP repo.
              toastService.emitToast({
                isSuccess: res[0].status.toLowerCase() === 'success',
                message: res[0].message
              });
              patchState(store, {
                isPriceDeleting: false,
                refreshPriceTable: true,
                isPriceTableLoading: true,
                isPriceDeleteDone: true,
                selectedPrices: []
              });

              setTimeout(() => {
                patchState(store, { isPriceDeleteDone: false, refreshPriceTable: false });
              }, 500);
            },
            error: () => {}
          })
        )
      ),
      exportDailyPriceReport: rxMethod<void>(
        pipe(
          tap(() => {
            patchState(store, { isPriceReportDownloading: true });
          }),
          switchMap(() => {
            return priceService.exportDailyPriceReport({
              priceComponentId: store.priceComponents()[0].priceComponentId || '',
              activeTableFilters: store.priceActiveFilters()
            });
          }),
          tapResponse({
            next: (file) => {
              saveAs(file.body as Blob, utilityService.getFileNameFromHeaders(file));
              patchState(store, { isPriceReportDownloading: false });
            },
            error: () => {}
          })
        )
      ),
      setPriceTableLoading(isLoading: boolean): void {
        patchState(store, { isPriceTableLoading: isLoading });
      },
      setPriceTableSelections(price: PriceComponentResult[]): void {
        patchState(store, { selectedPrices: price });
      },
      resetLoadingStates(initializePriceComponents = false): void {
        patchState(store, {
          isPriceTableLoading: false,
          isPriceSetDone: false,
          isRolesInitialized: false,
          isPriceListInitialized: true,
          isPriceComponentsInitialized: initializePriceComponents,
          isPriceSetInProgress: false,
          isPriceDeleting: false,
          isPriceReportDownloading: false
        });
      }
    })
  )
);
